import { Dialog, DialogBody, DialogHeader } from '@repo/ui';
import { numberUtil, dateUtil } from '@xtnd/web/shared/utils';
import { useDarkMode } from '@xtnd/web/shared/hooks/useDarkMode';

import { useMatterARContext } from '../common';
import { useAppealStatus } from '../../hooks';

export default function InvoiceDetailModal() {
  const { currentInvoice, closeInvoiceDetail } = useMatterARContext();
  const { isDark } = useDarkMode();
  const mode = isDark ? 'dark' : 'light';
  const { getAppealStatusLabel } = useAppealStatus(mode);

  return (
    <Dialog
      open={!!currentInvoice}
      onClose={closeInvoiceDetail}
      ariaLabel="Invoice Details"
      closeOnBackdrop
      closeOnEscape
      size="xxl"
    >
      <DialogHeader className="font-semibold text-xtnd-dark dark:text-xtnd-light text-lg">
        Invoice {currentInvoice?.invoiceNumber}{' '}
        {currentInvoice?.isSplitInvoice ? '(Split Invoice)' : ''}
      </DialogHeader>
      <DialogBody>
        <div className="px-4">
          <header className="py-2 border-b border-xtnd-dark">
            <h1 className="text-3xl font-bold uppercase">Invoice</h1>
            <h2 className="text-xtnd-dark dark:text-xtnd-light">
              Invoice #{currentInvoice?.invoiceNumber}
            </h2>
          </header>

          <main className="py-2">
            <div className="flex justify-between items-center text-xtnd-dark">
              <p className="uppercase text-xtnd-dark dark:text-xtnd-light">BILL TO:</p>
              <p className="text-sm">
                <strong className="font-semibold dark:text-xtnd-light">Invoice Date:</strong>
                <span className="ml-1 text-xtnd-dark dark:text-xtnd-light">
                  {currentInvoice?.invoiceDate
                    ? dateUtil.formatDate(
                        new Date(currentInvoice.invoiceDate),
                        dateUtil.DateFormat.US
                      )
                    : 'N/A'}
                </span>
              </p>
            </div>

            <div className="flex justify-between items-baseline text-xtnd-dark dark:text-xtnd-light">
              <strong className="flex-1">{currentInvoice?.clientName}</strong>
              <p className="text-sm">
                <strong className="font-semibold">Days Past Due:</strong>
                <span className="ml-1 text-xtnd-dark dark:text-xtnd-light">{`${currentInvoice?.daysPastDue} days`}</span>
              </p>
            </div>

            <div className="text-xtnd-dark dark:text-xtnd-light">
              <p>Matter: {currentInvoice?.matterDisplayNumber}</p>
            </div>

            <table className="min-w-full dark:border dark:border-xtnd-dark rounded-md mt-4 shadow-md">
              <thead className="bg-xtnd-light dark:bg-xtnd-dark">
                <tr>
                  <th className="px-6 py-3 text-left font-medium text-xtnd-dark dark:text-xtnd-light">
                    Description
                  </th>
                  <th className="px-6 py-3 text-right font-medium text-xtnd-dark dark:text-xtnd-light">
                    Amount
                  </th>
                </tr>
              </thead>

              <tbody className="text-xtnd-dark dark:text-xtnd-light">
                <tr>
                  <td className="px-6 py-4 whitespace-wrap text-sm text-gray-900 dark:text-xtnd-light">
                    {`Legal Services - ${currentInvoice?.matterDisplayNumber}`}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900 dark:text-xtnd-light">
                    {numberUtil.formatCurrency(currentInvoice?.outstandingBalance || 0)}
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="py-4 flex justify-end text-xtnd-dark dark:text-xtnd-light">
              <div className="flex flex-col gap-1 py-4 w-80" id="total-container">
                <div className="flex items-center justify-between">
                  <span className="font-medium">Subtotal:</span>
                  <span>{numberUtil.formatCurrency(currentInvoice?.total || 0)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Paid:</span>
                  <span>- {numberUtil.formatCurrency(currentInvoice?.paid || 0)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-medium">Credits Issued:</span>
                  <span>- {numberUtil.formatCurrency(currentInvoice?.creditsIssued || 0)}</span>
                </div>
                <hr className="border border-xtnd-dark my-2" />
                <div className="flex items-center justify-between">
                  <strong>Amount Due:</strong>
                  <span className="font-bold">
                    {numberUtil.formatCurrency(currentInvoice?.outstandingBalance || 0)}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-2 py-4 border border-xtnd-dark min-h-10 rounded-md text-sm text-xtnd-dark dark:text-xtnd-light">
              <div className="flex gap-4">
                <strong className="font-semibold">Payment Status:</strong>
                <span>{`${currentInvoice?.outstandingBalance ? 'Unpaid' : 'Paid'}`}</span>
              </div>
              {currentInvoice?.appealStatus && (
                <div className="mt-2 flex gap-4 flex items-center">
                  <strong className="font-semibold">Appeal Status:</strong>
                  <span>{getAppealStatusLabel(currentInvoice.appealStatus)}</span>
                </div>
              )}
            </div>
          </main>

          <footer className="mt-4 border-t border-xtnd-dark text-xtnd-dark dark:text-xtnd-light">
            <div className="flex justify-center pt-4">
              <p>Please remit payment within 30 days of invoice date.</p>
            </div>
          </footer>
        </div>
      </DialogBody>
    </Dialog>
  );
}
